/* The Stock Report's arithmetic — the design's `renderVals`, moved across as
 * it stands so a figure here can be read against the design line for line.
 *
 * `build` takes one month of register rows and the page's controls and
 * returns everything the two tabs draw: tiles, the total-stock line, the
 * closing-by-quality bars, the count table, produced against dispatched,
 * the register checks, and the waste chart and table. Nothing here touches
 * the DOM; the tabs only lay the result out. */

import { longDate, monthName, num, shortDate } from '../../lib/format'
import type { StockMonth, WasteStockRow, YarnStockRow } from '../../lib/api_stock'

/** The design's two props: the cover below which a count is flagged, and
 *  whether the yarn side is keyed by count ("20s KW") or by quality alone. */
export const LOW_COVER_DAYS = 7
export const STACK_BY: 'count' | 'type' = 'count'

export interface Controls {
  /** Day the "Closing yarn stock by quality" bars are read on; null = latest. */
  asOf: string | null
  /** Hovered day on the total-stock line. */
  vHov: string | null
  /** Hovered day on the waste chart. */
  wHov: string | null
}

const PAL = [
  'var(--color-accent-700)', 'var(--color-accent-400)', 'var(--color-neutral-800)',
  'var(--color-accent-900)', 'var(--color-neutral-500)', 'var(--color-accent-500)',
  'var(--color-neutral-600)', 'var(--color-accent-800)', 'var(--color-accent-600)',
  'var(--color-neutral-700)',
]

// ------------------------------------------------------------- formatting
const n = (v: number | null | undefined, d = 0): string =>
  (v === null || v === undefined || !isFinite(v) ? '–' : num(v, d))
/** Tonnes from a lakh kilos up, kilos below — the design's `kg`. */
export const kgT = (v: number | null | undefined): string =>
  (v === null || v === undefined ? '–' : Math.abs(v) >= 100000 ? n(v / 1000, 1) + ' t' : n(v) + ' kg')
const dayName = shortDate
const wdayName = (d: string) =>
  new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })
const signed = (v: number) => (v > 0 ? '+' : v < 0 ? '−' : '') + n(Math.abs(v))
const chgColor = (v: number | null) =>
  (v === null || v === 0 ? 'var(--color-neutral-600)' : v > 0 ? 'var(--color-accent-800)' : 'var(--color-neutral-900)')

/** The next 1 / 2 / 2.5 / 5 / 10 step at or above `v` — the top of an axis. */
const nice = (v: number) => {
  const p = Math.pow(10, Math.floor(Math.log10(v)))
  const m = v / p
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10) * p
}
const tickFmt = (v: number) => (v >= 1000 ? n(v / 1000, v % 1000 ? 1 : 0) + 't' : n(v))
const ticksAt = (fs: number[], top: number, from = 0): Tick[] =>
  fs.map((f) => ({ label: tickFmt(from + (top - from) * f), pos: f * 100 + '%' }))

/** The design's `wName`: the sheet's spelling of a waste stream as a label. */
export function wasteName(s: string): string {
  const k = String(s || '').toUpperCase().replace(/\s+/g, ' ').trim()
  const map: Record<string, string> = {
    'H.W': 'Hard waste', HARDWASTE: 'Hard waste', 'MICRO DUST': 'Micro dust', MICRODUCT: 'Micro dust',
    'GREEN BELT': 'Green belt', 'NOIL WASTE': 'Noil', 'CLOTH BALES': 'Cloth bales', LD: 'LD', FS: 'FS',
  }
  return map[k] || k.charAt(0) + k.slice(1).toLowerCase()
}

// ------------------------------------------------------------------ shapes
export interface Tick { label: string; pos: string }
export interface Tile { label: string; value: string; unit?: string; note?: string }

export interface TotHov {
  on: true; x: string; left: string; shift: string; title: string; value: string; delta: string
}
export interface QBar { label: string; bg: string; h: string; v: string; share: string; tip: string }
export interface CountRow {
  label: string; bg: string; open: string; prod: string; disp: string; other: string; close: string
  chg: string; chgColor: string; cover: string; coverCls: string; share: string; w: string
}
export interface CountTot {
  open: string; prod: string; disp: string; other: string; close: string; chg: string
  chgColor: string; cover: string
}
export interface FlowCol { p: string; d: string; tip: string }
export interface Check { d: string; day: string; label: string; prev: string; open: string; diff: string }
export interface WasteCol { d: string; op: number; segs: { h: string; bg: string }[] }
export interface WasteHov {
  on: true; left: string; shift: string; title: string; total: string
  rows: { label: string; bg: string; v: string }[]
}
export interface WasteRow {
  label: string; bg: string; open: string; prod: string; reuse: string; sold: string; close: string
  share: string; w: string
}

export interface Built {
  periodLine: string
  /** Day labels under the flow and waste charts: every `days` column. */
  dayLabels: string[]
  // yarn
  yKpis: Tile[]
  totLine: string; totArea: string; totTicks: Tick[]
  totDots: { left: string; bottom: string; size: string }[]
  totCols: string[]
  totHov: TotHov | null
  totSub: string
  trendLabels: string[]
  asOf: string | null; asOfLabel: string; asOfTotal: string; asOfCount: number
  asOfOptions: { key: string; label: string }[]
  qTicks: Tick[]; qBars: QBar[]
  countRows: CountRow[]; countTot: CountTot; countSub: string; countNote: string
  hasFlow: boolean; flowTicks: Tick[]; flowCols: FlowCol[]
  checks: Check[]
  // waste
  wKpis: Tile[]
  wTicks: Tick[]; wCols: WasteCol[]; wHov: WasteHov | null
  wLegend: { label: string; bg: string }[]
  wRows: WasteRow[]; wNote: string
  footerLine: string
}

type Y = YarnStockRow
type W = WasteStockRow & { type: string }

/** Sum a field over rows, or null when no row has it filled in. */
function sumOf<R>(rows: R[], f: (r: R) => number | null): number | null {
  const v = rows.map(f).filter((x): x is number => x !== null && x !== undefined)
  return v.length ? v.reduce((s, x) => s + x, 0) : null
}

export function build(D: StockMonth, S: Controls): Built {
  const month = D.month
  const lowCover = LOW_COVER_DAYS
  const stackBy = STACK_BY

  // ---------- yarn ----------
  const Y = D.yarn
  const days = D.days
  const yDays = [...new Set(Y.map((y) => y.date))].sort()
  const first = yDays[0], last = yDays[yDays.length - 1]
  const keyOf = (y: Y) => (stackBy === 'type' ? y.yarn_quality : y.count_ne + 's ' + y.yarn_quality)
  const keys = [...new Set(Y.map(keyOf))]
  const closeOn = (k: string, d: string) =>
    Y.filter((y) => y.date === d && keyOf(y) === k).reduce((s, y) => s + (y.closing || 0), 0)
  const lastClose: Record<string, number> = {}
  keys.forEach((k) => { lastClose[k] = closeOn(k, last) })
  keys.sort((a, b) => lastClose[b] - lastClose[a])
  const color: Record<string, string> = {}
  keys.forEach((k, i) => { color[k] = PAL[i % PAL.length] })
  const hasFlowData = Y.some((y) => y.produced !== null || y.dispatched !== null)

  const dayTot = (d: string) => keys.reduce((s, k) => s + closeOn(k, d), 0)
  const xPos = (d: string) => (days.length > 1 ? (days.indexOf(d) + 0.5) / days.length * 100 : 50)
  const step = Math.ceil(days.length / 15)
  const dayLabels = days.map((d, i) => (i % step === 0 || i === days.length - 1 ? String(+d.slice(8)) : ''))

  const firstOpen = (k: string) => {
    const r = Y.filter((y) => keyOf(y) === k && y.date === first)
    if (!r.length) return null
    return r.reduce((s, y) => s + (y.opening !== null ? y.opening : (y.closing || 0)), 0)
  }
  const nDays = yDays.length || 1
  const totClose = keys.reduce((s, k) => s + lastClose[k], 0) || 1
  const rowsK = keys.map((k) => {
    const rows = Y.filter((y) => keyOf(y) === k)
    const open = firstOpen(k)
    const prod = sumOf(rows, (y) => y.produced), disp = sumOf(rows, (y) => y.dispatched)
    const iss = sumOf(rows, (y) => y.issued), adj = sumOf(rows, (y) => y.adjustment)
    const other = iss === null && adj === null ? null : (adj || 0) - (iss || 0)
    const close = lastClose[k], chg = open === null ? null : close - open
    const daily = disp ? disp / nDays : 0
    const cover = daily ? close / daily : null
    return { k, open, prod, disp, other, close, chg, cover }
  })
  const coverTxt = (c: number | null) => (c === null ? 'no dispatch' : c > 365 ? '365+ d' : n(c, 0) + ' d')
  const coverCls = (c: number | null) => (c === null ? 'tag-outline' : c < lowCover ? 'tag-accent' : 'tag-neutral')
  const countRows: CountRow[] = rowsK.map((r) => ({
    label: r.k, bg: color[r.k], open: n(r.open), prod: n(r.prod), disp: n(r.disp),
    other: r.other === null ? '–' : (r.other > 0 ? '+' : '') + n(r.other),
    close: n(r.close), chg: r.chg === null ? '–' : signed(r.chg), chgColor: chgColor(r.chg),
    cover: hasFlowData ? coverTxt(r.cover) : '–', coverCls: hasFlowData ? coverCls(r.cover) : 'tag-outline',
    share: n(r.close / totClose * 100, 1) + '%', w: (r.close / totClose * 100).toFixed(1) + '%',
  }))
  const T = (f: 'open' | 'prod' | 'disp' | 'other') =>
    (rowsK.some((r) => r[f] !== null) ? rowsK.reduce((s, r) => s + (r[f] || 0), 0) : null)
  const tOpen = T('open'), tProd = T('prod'), tDisp = T('disp'), tOther = T('other')
  const tClose = rowsK.reduce((s, r) => s + r.close, 0)
  const tChg = tOpen === null ? null : tClose - tOpen
  const tCover = tDisp ? tClose / (tDisp / nDays) : null
  const countTot: CountTot = {
    open: n(tOpen), prod: n(tProd), disp: n(tDisp),
    other: tOther === null ? '–' : (tOther > 0 ? '+' : '') + n(tOther), close: n(tClose),
    chg: tChg === null ? '–' : signed(tChg), chgColor: chgColor(tChg),
    cover: hasFlowData ? coverTxt(tCover) : '–',
  }
  const slow = rowsK.filter((r) => r.close > 0 && (r.disp === null || r.disp === 0))
  const yKpis: Tile[] = [
    { label: 'Closing yarn stock', value: n(tClose / 1000, 1), unit: 't',
      note: 'on ' + (last ? dayName(last) : '–') + ' · ' + keys.filter((k) => lastClose[k] > 0).length
        + ' ' + (stackBy === 'type' ? 'types' : 'counts') + ' in stock' },
    { label: 'Change in the month',
      value: tChg === null ? '–' : (tChg > 0 ? '+' : tChg < 0 ? '−' : '') + n(Math.abs(tChg) / 1000, 1),
      unit: tChg === null ? '' : 't',
      note: tOpen === null ? 'openings not recorded this month' : 'from ' + n(tOpen / 1000, 1) + ' t at opening' },
    { label: 'Produced into stock', value: tProd === null ? '–' : n(tProd / 1000, 1), unit: tProd === null ? '' : 't',
      note: tProd === null ? 'not in this month’s register' : n(tProd / nDays / 1000, 1) + ' t a day' },
    { label: 'Dispatched', value: tDisp === null ? '–' : n(tDisp / 1000, 1), unit: tDisp === null ? '' : 't',
      note: tDisp === null ? 'not in this month’s register'
        : n(tDisp / nDays / 1000, 1) + ' t a day · ' + (tProd ? n(tDisp / tProd * 100, 0) + '% of production' : '') },
    { label: 'Days of cover', value: tCover === null ? '–' : n(tCover, 0), unit: tCover === null ? '' : 'days',
      note: tCover === null ? 'needs dispatch figures' : 'closing stock at this month’s dispatch rate' },
  ]
  const countSub = 'From ' + (first ? dayName(first) : '–') + ' to ' + (last ? dayName(last) : '–') + ' · kg'
  const countNote = (hasFlowData
    ? 'Opening is the first day’s opening; produced, dispatched and issued are summed over the month; closing is the last day’s calculated closing. Days of cover divides closing stock by the average daily dispatch of that count this month — anything under ' + lowCover + ' days is highlighted.'
    : 'This month’s register only records closing stock by count, so produced, dispatched and cover are not available.')
    + (slow.length ? ' ' + slow.length + ' ' + (slow.length === 1 ? 'count holds' : 'counts hold')
      + ' stock with nothing dispatched this month: ' + slow.map((r) => r.k).join(', ') + '.' : '')

  // total stock line
  const tX = (i: number) => (i + 0.5) / (yDays.length || 1) * 1000
  const totVals = yDays.map((d) => dayTot(d))
  const vLo = Math.min(...totVals), vHi = Math.max(...totVals)
  const vPad = (vHi - vLo) * 0.15 || vHi * 0.05 || 1
  const v0 = Math.max(0, Math.floor((vLo - vPad) / 10000) * 10000)
  const v1 = Math.ceil((vHi + vPad) / 10000) * 10000
  const vY = (v: number) => 240 - (v - v0) / ((v1 - v0) || 1) * 240
  const totLine = totVals.map((v, i) => tX(i).toFixed(1) + ',' + vY(v).toFixed(1)).join(' ')
  const totArea = totVals.length
    ? tX(0).toFixed(1) + ',240 ' + totLine + ' ' + tX(totVals.length - 1).toFixed(1) + ',240' : ''
  const totTicks = ticksAt([0, 0.25, 0.5, 0.75, 1], v1, v0)
  const vHovI = S.vHov ? yDays.indexOf(S.vHov) : -1
  const totDots = totVals.map((v, i) => ({
    left: tX(i) / 10 + '%', bottom: (v - v0) / ((v1 - v0) || 1) * 100 + '%',
    size: i === vHovI ? '11px' : yDays.length > 20 ? '6px' : '8px',
  }))
  const totHov: TotHov | null = vHovI >= 0 ? {
    on: true, x: tX(vHovI).toFixed(1), left: tX(vHovI) / 10 + '%',
    shift: tX(vHovI) > 620 ? 'translateX(calc(-100% - 10px))' : 'translateX(10px)',
    title: wdayName(yDays[vHovI]), value: kgT(totVals[vHovI]),
    delta: vHovI > 0
      ? ((totVals[vHovI] - totVals[vHovI - 1]) >= 0 ? '+' : '−')
        + kgT(Math.abs(totVals[vHovI] - totVals[vHovI - 1])) + ' vs previous day'
      : 'first day of the month',
  } : null
  const totSub = yDays.length
    ? 'Closing stock, all qualities · peak ' + kgT(vHi) + ' on ' + dayName(yDays[totVals.indexOf(vHi)])
      + ' · low ' + kgT(vLo) + ' on ' + dayName(yDays[totVals.indexOf(vLo)])
    : ''
  const tStep = Math.ceil(yDays.length / 15)
  const trendLabels = yDays.map((d, i) => (i % tStep === 0 || i === yDays.length - 1 ? String(+d.slice(8)) : ''))

  // closing by quality as of a chosen day
  const asOf = S.asOf && yDays.includes(S.asOf) ? S.asOf : (last ?? null)
  const qVals = keys.map((k) => ({ k, v: asOf ? closeOn(k, asOf) : 0 })).filter((q) => q.v > 0)
    .sort((a, b) => b.v - a.v)
  const qTot = qVals.reduce((s, q) => s + q.v, 0) || 1
  const qMax = nice(Math.max(1, ...qVals.map((q) => q.v)) * 1.12)
  const qTicks = ticksAt([0, 0.25, 0.5, 0.75, 1], qMax)
  const qBars: QBar[] = qVals.map((q) => ({
    label: q.k, bg: color[q.k], h: (q.v / qMax * 100).toFixed(2) + '%', v: kgT(q.v),
    share: n(q.v / qTot * 100, 1) + '%', tip: q.k + ' · ' + n(q.v) + ' kg on ' + (asOf ? dayName(asOf) : '–'),
  }))

  // produced vs dispatched
  const prodOn = (d: string) => Y.filter((y) => y.date === d).reduce((s, y) => s + (y.produced || 0), 0)
  const dispOn = (d: string) => Y.filter((y) => y.date === d).reduce((s, y) => s + (y.dispatched || 0), 0)
  const fMax = nice(Math.max(1, ...days.map((d) => Math.max(prodOn(d), dispOn(d)))) * 1.05)
  const flowTicks = ticksAt([0, 1 / 3, 2 / 3, 1], fMax)
  const flowCols: FlowCol[] = days.map((d) => ({
    p: (prodOn(d) / fMax * 100).toFixed(2) + '%', d: (dispOn(d) / fMax * 100).toFixed(2) + '%',
    tip: dayName(d) + ' · produced ' + kgT(prodOn(d)) + ' · dispatched ' + kgT(dispOn(d)),
  }))

  // continuity checks: today's opening against yesterday's closing
  const checks: Check[] = []
  if (hasFlowData) {
    const byKey: Record<string, Y[]> = {}
    Y.forEach((y) => { const k = y.count_ne + 's ' + y.yarn_quality; (byKey[k] = byKey[k] || []).push(y) })
    Object.entries(byKey).forEach(([k, a]) => {
      a.sort((p, q) => (p.date < q.date ? -1 : 1))
      for (let i = 1; i < a.length; i++) {
        if (a[i].opening === null || a[i - 1].closing === null) continue
        const diff = (a[i].opening as number) - a[i - 1].closing
        if (Math.abs(diff) > 1) {
          checks.push({ d: a[i].date, day: dayName(a[i].date), label: k, prev: n(a[i - 1].closing),
            open: n(a[i].opening), diff: (diff > 0 ? '+' : '−') + n(Math.abs(diff)) })
        }
      }
    })
    checks.sort((p, q) => (p.d < q.d ? -1 : 1))
  }

  // ---------- waste ----------
  const W: W[] = D.waste.map((w) => ({ ...w, type: wasteName(w.waste_type) }))
  const wDays = [...new Set(W.map((w) => w.date))].sort()
  const wTypes = [...new Set(W.map((w) => w.type))]
  const wSum = (t: string, f: (w: W) => number | null) => sumOf(W.filter((w) => w.type === t), f)
  const wProd: Record<string, number> = {}
  wTypes.forEach((t) => { wProd[t] = wSum(t, (w) => w.produced) || 0 })
  wTypes.sort((a, b) => wProd[b] - wProd[a])
  const wColor: Record<string, string> = {}
  wTypes.forEach((t, i) => { wColor[t] = PAL[i % PAL.length] })
  const wOn = (t: string, d: string) =>
    W.filter((w) => w.type === t && w.date === d).reduce((s, w) => s + (w.produced || 0), 0)
  const wDayTot = (d: string) => wTypes.reduce((s, t) => s + wOn(t, d), 0)
  const wMax = nice(Math.max(1, ...days.map(wDayTot)) * 1.05)
  const wTicks = ticksAt([0, 1 / 3, 2 / 3, 1], wMax)
  const wHovD = S.wHov && days.includes(S.wHov) ? S.wHov : null
  const wCols: WasteCol[] = days.map((d) => ({
    d, op: wHovD && wHovD !== d ? 0.45 : 1,
    segs: wTypes.map((t) => ({ h: (wOn(t, d) / wMax * 100).toFixed(2) + '%', bg: wColor[t] }))
      .filter((s) => parseFloat(s.h) > 0),
  }))
  const wHov: WasteHov | null = wHovD ? {
    on: true, left: xPos(wHovD) + '%',
    shift: xPos(wHovD) > 62 ? 'translateX(calc(-100% - 10px))' : 'translateX(10px)',
    title: dayName(wHovD), total: kgT(wDayTot(wHovD)),
    rows: wTypes.map((t) => ({ label: t, bg: wColor[t], v: kgT(wOn(t, wHovD)) })).filter((r) => r.v !== '0 kg'),
  } : null
  const wFirst = wDays[0], wLast = wDays[wDays.length - 1]
  const wTotProd = wTypes.reduce((s, t) => s + wProd[t], 0) || 0
  const wHasStock = W.some((w) => w.closing !== null)
  const wRows: WasteRow[] = wTypes.map((t) => {
    const o = W.find((w) => w.type === t && w.date === wFirst)
    const c = W.filter((w) => w.type === t && w.date === wLast)[0]
    return {
      label: t, bg: wColor[t], open: n(o ? o.opening : null), prod: n(wProd[t]),
      reuse: n(wSum(t, (w) => w.reused)), sold: n(wSum(t, (w) => w.sold)), close: n(c ? c.closing : null),
      share: wTotProd ? n(wProd[t] / wTotProd * 100, 1) + '%' : '–',
      w: wTotProd ? (wProd[t] / wTotProd * 100).toFixed(1) + '%' : '0%',
    }
  })
  const wCloseTot = wHasStock
    ? W.filter((w) => w.date === wLast).reduce((s, w) => s + (w.closing || 0), 0) : null
  const wSold = wTypes.reduce((s, t) => s + (wSum(t, (w) => w.sold) || 0), 0)
  const wReuse = wTypes.reduce((s, t) => s + (wSum(t, (w) => w.reused) || 0), 0)
  const monthYarnProd = tProd !== null ? tProd : null
  const wKpis: Tile[] = [
    { label: 'Waste produced', value: n(wTotProd / 1000, 1), unit: 't',
      note: n(wTotProd / (wDays.length || 1)) + ' kg a day'
        + (monthYarnProd ? ' · ' + n(wTotProd / monthYarnProd * 100, 1) + '% of yarn produced' : '') },
    { label: 'Closing waste stock', value: wCloseTot === null ? '–' : n(wCloseTot / 1000, 1),
      unit: wCloseTot === null ? '' : 't',
      note: wCloseTot === null ? 'stock not recorded this month' : 'on ' + dayName(wLast) },
    { label: 'Sold / disposed', value: wHasStock ? n(wSold / 1000, 1) : '–', unit: wHasStock ? 't' : '',
      note: wHasStock ? (wReuse ? n(wReuse) + ' kg reused in the process' : 'nothing reused this month')
        : 'not recorded this month' },
    { label: 'Largest stream', value: wTypes[0] || '–', unit: '',
      note: wTypes[0] ? n(wProd[wTypes[0]] / (wTotProd || 1) * 100, 0) + '% of waste produced' : '' },
  ]
  const wNote = wHasStock
    ? 'Opening and closing from the first and last day of the month in the waste register; produced, reused and sold are summed. The register books all streams under Blow Room, so this is plant-level waste — including LD, FS and sweepings, not just spinning-floor waste.'
    : 'This month’s report only records waste produced by type (both units), not stock movements.'

  return {
    periodLine: monthName(month, true) + ' · yarn and waste stock registers · ' + days.length + ' days',
    dayLabels,
    yKpis, totLine, totArea, totTicks, totDots, totCols: yDays, totHov, totSub, trendLabels,
    asOf, asOfLabel: asOf ? longDate(asOf) : '–',
    asOfTotal: kgT(qVals.reduce((s, q) => s + q.v, 0)), asOfCount: qVals.length,
    asOfOptions: yDays.slice().reverse().map((d) => ({ key: d, label: dayName(d) + (d === last ? ' (latest)' : '') })),
    qTicks, qBars,
    countRows, countTot, countSub, countNote,
    hasFlow: hasFlowData, flowTicks, flowCols,
    checks,
    wKpis, wTicks, wCols, wHov, wLegend: wTypes.map((t) => ({ label: t, bg: wColor[t] })), wRows, wNote,
    footerLine: 'Source: the daily machine tracker’s yarn and waste stock registers for ' + D.label
      + ' · ' + days.length + ' days on file · produced is the autoconers’ output for the count that day; closing is opening + produced − dispatched − issued + adjustment, or the physical count where one was taken.',
  }
}
